import { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import type { ImportHistoryRecord, ImportHistoryResponse, ImportJobTypeTotals } from '@app-types/import';

interface UseImportJobsPageDataOptions {
  queryKey: QueryKey;
  queryFn: () => Promise<ImportHistoryResponse>;
  pageSize: number;
  enabled?: boolean;
}

export interface UseImportJobsPageDataReturn {
  jobs: ImportHistoryRecord[];
  isLoading: boolean;
  error: Error | null;
  isFetching: boolean;
  response: ImportHistoryResponse | undefined;
  filterCounts: Record<string, number>;
  totalItems: number;
  totalPages: number;
  refreshData: () => void;
}

export function useImportJobsPageData(options: UseImportJobsPageDataOptions): UseImportJobsPageDataReturn {
  const { queryKey, queryFn, pageSize, enabled = true } = options;
  const queryClient = useQueryClient();

  const {
    data: response,
    isLoading,
    error,
    isFetching
  } = useQuery<ImportHistoryResponse>({
    queryKey,
    queryFn,
    staleTime: 0,
    refetchOnMount: 'always',
    placeholderData: keepPreviousData,
    enabled
  });

  const jobs = useMemo(() => response?.records || [], [response]);

  const [lastJobTypeTotals, setLastJobTypeTotals] = useState<ImportJobTypeTotals | null>(null);
  useEffect(() => {
    if (response?.jobTypeTotals) {
      setLastJobTypeTotals(response.jobTypeTotals);
    }
  }, [response?.jobTypeTotals]);

  const filterCounts: Record<string, number> = useMemo(() => {
    const totals = response?.jobTypeTotals || lastJobTypeTotals;

    if (!totals) {
      return { all: response?.total || 0, scheduled: 0, manual: 0, on_demand: 0 };
    }

    return {
      all: totals.all || response?.total || 0,
      scheduled: totals.scheduled || 0,
      manual: totals.manual || 0,
      on_demand: totals.on_demand || 0,
    };
  }, [response, lastJobTypeTotals]);

  const totalItems = response?.total || 0;
  const totalPages = Math.ceil(totalItems / (pageSize || 1));

  const refreshData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [queryKey[0]] });
  }, [queryClient, queryKey]);

  return {
    jobs,
    isLoading,
    error: error as Error | null,
    isFetching,
    response,
    filterCounts,
    totalItems,
    totalPages,
    refreshData,
  };
}
